import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { InfoBanner } from "@/lib/Components/Banners/InfoBanner";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { supplierOfficeListQuery } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { MultiSelectInput } from "@/lib/Components/Form/Inputs/MultiSelectInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { addDestinationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { relocationListQuery } from "@/app/Relocations/GraphQL/relocationListQuery";
import {
  QueryRelocationsWhereColumn,
  QuerySupplierOfficesOrderByColumn,
  RelocationStatus,
  SortOrder,
  SqlOperator,
} from "@/gql/graphql";
import { useRelocation } from "@/app/Relocations/Hooks/useRelocation";

export function AddDestinationsDialog({
  relocationId,
}: {
  relocationId: string;
}) {
  const { close } = useDialogState();
  const relocation = useRelocation(relocationId);
  const { mutateAsync } = useGqlMutation(addDestinationMutation);

  const supplierId = relocation.supplier.id;

  const { data: offices } = useSuspenseGqlQuery(supplierOfficeListQuery, {
    supplier_id: supplierId,
    page: 1,
    first: 200,
    archived: false,
    orderBy: [
      {
        column: QuerySupplierOfficesOrderByColumn.Name,
        order: SortOrder.Asc,
      },
    ],
  });

  const { data: relocations } = useSuspenseGqlQuery(relocationListQuery, {
    first: 200,
    page: 1,
    where: {
      AND: [
        {
          column: QueryRelocationsWhereColumn.GroupId,
          value: relocation.group.id,
        },
        {
          column: QueryRelocationsWhereColumn.Id,
          operator: SqlOperator.Neq,
          value: relocationId,
        },
        {
          column: QueryRelocationsWhereColumn.Status,
          value: RelocationStatus.Ready,
        },
      ],
    },
  });

  const disabledOfficeIds = relocations.relocations.data.map((relocation) => {
    return relocation.deliveryOffice.id;
  });

  return (
    <BaseDialog title={`Add destinations to ${relocation.reference}`}>
      <div className="space-y-12">
        <InfoBanner>
          Allow the for the vehicle to be dropped off at multiple locations
        </InfoBanner>

        <GenericForm
          initialValues={{
            ids: [],
          }}
          onSubmit={async (values) => {
            await mutateAsync({
              relocationId,
              officeIds: values.ids,
            });

            await close();
          }}
        >
          <MultiSelectInput
            className="col-span-full"
            name="ids"
            label="Destinations"
            options={offices.supplierOffices.data
              .filter((office) => {
                return (
                  office.id !== relocation.departureOffice.id &&
                  office.id !== relocation.deliveryOffice.id &&
                  !disabledOfficeIds.includes(office.id)
                );
              })
              .map((office) => {
                return {
                  label: office.name,
                  value: office.id,
                };
              })}
          />

          <DialogSubmitButton text="Add destinations" />
        </GenericForm>
      </div>
    </BaseDialog>
  );
}
